import { ReactElement, Suspense, useEffect, useState } from 'react'
import { LoadingSpinner } from '../../components/shared/LoadingSpinner'
import { AdminButton } from '../components/controls/AdminButton'
import { Button, Checkbox, Input, message, Select, Skeleton, Table } from 'antd'
import { EventStats } from '../models/event'
import { useNavigate } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import { errorHandler } from '../../utils/helper'
import { ColumnsType } from 'antd/es/table'
import dayjs from 'dayjs'
import { fetchEvents, updateEventStatus } from '../../adminModule/services/eventStats'
import { EventStatus } from '../../constants/common'

const STORAGE_KEY = 'adminEventListingVisibleColumns'
const HIDDEN_BY_DEFAULT_COLUMN_KEYS = ['id', 'EndDate', 'ShortName']

export function AdminEventListing(): ReactElement {
  const navigate = useNavigate()
  const { getAccessTokenSilently } = useAuth0()
  const [isLoading, setIsLoading] = useState(true)
  const [eventStats, setEventStats] = useState<EventStats[]>([])
  const [filteredDataSource, setFilteredDataSource] = useState<EventStats[]>([])
  const [searchText, setSearchText] = useState('')
  const [selectedColumnKeys, setSelectedColumnKeys] = useState<string[]>([])
  const [showFieldSelector, setShowFieldSelector] = useState(false)
  const [savingIds, setSavingIds] = useState<number[]>([])

  const MAX_INVOICE_LENGTH = 100

  const eventStatusOptions = [
    { label: EventStatus[EventStatus.Planned], value: EventStatus.Planned },
    { label: EventStatus[EventStatus.Completed], value: EventStatus.Completed },
    { label: EventStatus[EventStatus.Invoiced], value: EventStatus.Invoiced },
    { label: EventStatus[EventStatus.Paid], value: EventStatus.Paid },
  ]

  const columns: ColumnsType<EventStats> = [
    {
      key: 'save',
      title: 'Save',
      dataIndex: 'save',
      render: (_value: unknown, record: EventStats) => (
        <Button
          className="stats-save-button border border-slate-500"
          onClick={() => handleSave(record)}
          loading={savingIds.includes(record.Id)}
        >
          Save
        </Button>
      ),
    },
    {
      key: 'StatusId',
      title: 'Status',
      dataIndex: 'StatusId',
      align: 'right',
      render: (_val: number, record: EventStats) => (
        <Select<number>
          options={eventStatusOptions}
          value={record.StatusId ?? 0}
          onChange={(value) => handleStatusChange(record.Id, value)}
          style={{ width: 120 }}
          dropdownStyle={{ textAlign: 'left' }}
          className="event-status-select"
        />
      ),
      sorter: (a, b) => a.StatusId - b.StatusId,
      sortDirections: ['descend', 'ascend'],
    },
    {
      key: 'InvoiceNumber',
      title: 'Invoice Number',
      dataIndex: 'InvoiceNumber',
      align: 'right',
      render: (_val: string, record: EventStats) => (
        <Input
          maxLength={MAX_INVOICE_LENGTH}
          value={record.InvoiceNumber ?? ''}
          onChange={(event) => handleInvoiceChange(record.Id, event.target.value)}
        />
      ),
      sorter: (a, b) => (a.InvoiceNumber ?? '').localeCompare(b.InvoiceNumber ?? ''),
      sortDirections: ['descend', 'ascend'],
    },
    {
      key: 'id',
      title: 'Event Id',
      dataIndex: 'Id',
      width: 120,
      sorter: (a, b) => a.Id - b.Id,
      render: (id) => <div className="pr-2 pl-2 pt-1 pb-1">{id}</div>,
    },
    {
      key: 'StartDate',
      title: 'Start Date',
      dataIndex: 'StartDate',
      render: (date) => <div className="pr-2 pl-2 pt-1 pb-1 whitespace-nowrap">{dayjs(date).format('YYYY-MM-DD')}</div>,
      sorter: (a, b) => (dayjs(a.StartDate).isBefore(b.StartDate) ? -1 : 1),
      sortDirections: ['descend', 'ascend'],
    },
    {
      key: 'EndDate',
      title: 'End Date',
      dataIndex: 'EndDate',
      render: (date) => <div className="pr-2 pl-2 pt-1 pb-1 whitespace-nowrap">{dayjs(date).format('YYYY-MM-DD')}</div>,
      sorter: (a, b) => (dayjs(a.EndDate).isBefore(b.EndDate) ? -1 : 1),
      sortDirections: ['descend', 'ascend'],
    },
    {
      key: 'AccountName',
      title: 'Account Name',
      dataIndex: 'AccountName',
      sorter: (a, b) => a.AccountName.localeCompare(b.AccountName),
      sortDirections: ['descend', 'ascend'],
      render: (name) => (
        <div className="pr-2 pl-2 pt-1 pb-1 font-regular bg-neutral-100 border border-slate-500 rounded-r-3xl">
          {name}
        </div>
      ),
    },
    {
      key: 'Name',
      title: 'Event Name',
      dataIndex: 'Name',
      sorter: (a, b) => a.Name.localeCompare(b.Name),
      sortDirections: ['descend', 'ascend'],
      render: (name) => <div className="pr-2 pl-2 pt-1 pb-1 font-bold">{name}</div>,
    },
    {
      key: 'ShortName',
      title: 'Event Short Name',
      dataIndex: 'ShortName',
      sorter: (a, b) => a.ShortName.localeCompare(b.ShortName),
      sortDirections: ['descend', 'ascend'],
      render: (name) => <div className="pr-2 pl-2 pt-1 pb-1 font-regular">{name}</div>,
    },
    {
      key: 'RecordCount',
      title: 'Record Count',
      dataIndex: 'RecordCount',
      align: 'right',
      render: (val: number) => <div className="pr-2 pl-2 pt-1 pb-1">{val.toLocaleString()}</div>,
      sorter: (a, b) => a.RecordCount - b.RecordCount,
      sortDirections: ['descend', 'ascend'],
    },
    {
      key: 'CheckedInCount',
      title: 'Checked In Count',
      dataIndex: 'CheckedInCount',
      align: 'right',
      render: (val: number) => <div className="pr-2 pl-2 pt-1 pb-1">{val.toLocaleString()}</div>,
      sorter: (a, b) => a.CheckedInCount - b.CheckedInCount,
      sortDirections: ['descend', 'ascend'],
    },
    {
      key: 'PrintCount',
      title: 'Print Count',
      dataIndex: 'PrintCount',
      align: 'right',
      render: (val: number) => <div className="pr-2 pl-2 pt-1 pb-1">{val.toLocaleString()}</div>,
      sorter: (a, b) => a.PrintCount - b.PrintCount,
      sortDirections: ['descend', 'ascend'],
    },
    {
      key: 'PrintAgentCount',
      title: 'Print Agent Count',
      dataIndex: 'PrintAgentCount',
      align: 'right',
      render: (val: string) => <div className="pr-2 pl-2 pt-1 pb-1">{val}</div>,
      sorter: (a, b) => Number(a.PrintAgentCount ?? 0) - Number(b.PrintAgentCount ?? 0),
      sortDirections: ['descend', 'ascend'],
    },
    {
      key: 'PrinterCount',
      title: 'Printer Count',
      dataIndex: 'DeviceCount',
      align: 'right',
      render: (val: number) => <div className="pr-2 pl-2 pt-1 pb-1">{val.toLocaleString()}</div>,
      sorter: (a, b) => a.DeviceCount - b.DeviceCount,
      sortDirections: ['descend', 'ascend'],
    },
    {
      key: 'SessionCount',
      title: 'Session Count',
      dataIndex: 'SessionCount',
      align: 'right',
      render: (val: number) => <div className="pr-2 pl-2 pt-1 pb-1">{val.toLocaleString()}</div>,
      sorter: (a, b) => a.SessionCount - b.SessionCount,
      sortDirections: ['descend', 'ascend'],
    },
    {
      key: 'DeviceCount',
      title: 'Device Count',
      dataIndex: 'DeviceCount',
      align: 'right',
      render: (val: number) => <div className="pr-2 pl-2 pt-1 pb-1">{val.toLocaleString()}</div>,
      sorter: (a, b) => a.DeviceCount - b.DeviceCount,
      sortDirections: ['descend', 'ascend'],
    },
    {
      key: 'ScanCount',
      title: 'Scan Count',
      dataIndex: 'ScanCount',
      align: 'right',
      render: (val: number) => <div className="pr-2 pl-2 pt-1 pb-1">{val.toLocaleString()}</div>,
      sorter: (a, b) => a.ScanCount - b.ScanCount,
      sortDirections: ['descend', 'ascend'],
    },
  ]

  const selectableColumnKeys = columns.filter((col) => col.key !== 'save').map((col) => String(col.key))
  const defaultVisibleColumnKeys = selectableColumnKeys.filter((key) => !HIDDEN_BY_DEFAULT_COLUMN_KEYS.includes(key))
  const fieldOptions = columns
    .filter((col) => col.key !== 'save')
    .map((col) => ({ label: col.title as string, value: String(col.key) }))

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored) {
      try {
        const parsed = JSON.parse(stored) as string[]
        const validKeys = parsed.filter((key) => selectableColumnKeys.includes(key))
        setSelectedColumnKeys(validKeys.length ? validKeys : defaultVisibleColumnKeys)
      } catch {
        setSelectedColumnKeys(defaultVisibleColumnKeys)
      }
    } else {
      setSelectedColumnKeys(defaultVisibleColumnKeys)
    }
  }, [])

  useEffect(() => {
    getAccessTokenSilently()
      .then(async (token) => {
        const data = await fetchEvents(token)
        const normalized = data.map((item) => ({
          ...item,
          StatusId: item.StatusId ?? 0,
        }))
        normalized.sort((a, b) => (dayjs(a.Name).isBefore(b.Name) ? 1 : -1))
        setEventStats(normalized)
        setFilteredDataSource(normalized.filter((item) => item.Name.length > 0))
      })
      .catch(errorHandler)
      .finally(() => setIsLoading(false))
  }, [getAccessTokenSilently])

  useEffect(() => {
    const lowercasedSearchText = searchText.toLowerCase()

    const filteredData = eventStats
      ?.filter((item) => {
        const hasIdMatch = item.Id.toString().includes(lowercasedSearchText)
        const hasNameMatch = item?.Name?.toLowerCase().includes(lowercasedSearchText)
        const hasAccountMatch = item?.AccountName?.toLowerCase().includes(lowercasedSearchText)
        const hasInvoiceMatch = (item?.InvoiceNumber ?? '').toLowerCase().includes(lowercasedSearchText)
        const hasShortNameMatch = item?.ShortName?.toLowerCase().includes(lowercasedSearchText)

        return hasIdMatch || hasNameMatch || hasAccountMatch || hasInvoiceMatch || hasShortNameMatch
      })
      .sort((a, b) => (dayjs(a.StartDate).isBefore(b.StartDate) ? 1 : -1))
    setFilteredDataSource(filteredData)
  }, [eventStats, searchText])

  const handleFieldSelectionChange = (checkedValues: Array<string>) => {
    const values = checkedValues.length ? checkedValues : defaultVisibleColumnKeys
    setSelectedColumnKeys(values)
    localStorage.setItem(STORAGE_KEY, JSON.stringify(values))
  }

  const visibleColumns = columns.filter((col) => col.key === 'save' || selectedColumnKeys.includes(String(col.key)))

  function getEventStatusRowClassName(statusId: number | undefined): string {
    switch (statusId ?? EventStatus.Planned) {
      case EventStatus.Invoiced:
        return 'event-status-invoiced'
      case EventStatus.Paid:
        return 'event-status-paid'
      default:
        return ''
    }
  }

  function handleInvoiceChange(eventId: number, value: string) {
    const truncatedValue = value.slice(0, MAX_INVOICE_LENGTH)
    setEventStats((current) =>
      current.map((row) => (row.Id === eventId ? { ...row, InvoiceNumber: truncatedValue } : row))
    )
  }

  function handleStatusChange(eventId: number, statusId: number) {
    setEventStats((current) => current.map((row) => (row.Id === eventId ? { ...row, StatusId: statusId } : row)))
  }

  async function handleSave(record: EventStats) {
    setSavingIds((ids) => [...ids, record.Id])
    try {
      const token = await getAccessTokenSilently()
      await updateEventStatus(token, {
        AccountId: record.AccountId,
        EventId: record.Id,
        InvoiceNumber: record.InvoiceNumber ?? '',
        StatusId: record.StatusId ?? 0,
      })
      message.success('Event status saved')
    } catch (error) {
      message.error('Failed to save event status')
    } finally {
      setSavingIds((ids) => ids.filter((id) => id !== record.Id))
    }
  }

  return (
    <Suspense fallback={<LoadingSpinner isLoading={true} label="Loading..." />}>
      <div className="container mx-auto">
        <div className="flex justify-center items-center bg-SecondaryBgWhite-100 rounded-lg px-6 my-2 py-2">
          <h1 className="text-xl font-semibold text-PrimaryDarkBlue-100 flex-1">Event Listing</h1>
          <AdminButton label={'Close'} onClick={() => navigate('/admin/dashboard')} />
        </div>
        <div className="flex-col justify-center items-center bg-SecondaryBgWhite-100 rounded-lg px-6 my-3 h-full py-2">
          <div className="flex justify-center items-center w-full py-2">
            <h1 className="text-xl font-semibold text-PrimaryDarkBlue-100 flex-1">Summary</h1>
            <Input.Search
              value={searchText}
              style={{ width: 'auto' }}
              placeholder="Search..."
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
            />
          </div>
          <div className="mt-1">
            <div className="adminContainerRow">
              <div className="adminContainer overflow-x-hidden">
                <div className="sticky top-0 z-10 bg-transparent border-b border-slate-200 px-4 py-3">
                  <div className="flex items-center justify-end">
                    <div className="relative ml-auto">
                      <button
                        type="button"
                        className="inline-flex items-center justify-center rounded border border-slate-300 bg-white px-3 py-1 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50"
                        onClick={() => setShowFieldSelector((prev) => !prev)}
                      >
                        Fields
                      </button>

                      {showFieldSelector && (
                        <div
                          className="absolute right-0 z-20 mt-2 rounded border border-slate-200 bg-white p-4 shadow-lg"
                          style={{ minWidth: 400, width: 400 }}
                          role="dialog"
                          aria-label="Field selector"
                        >
                          <div className="flex items-start justify-between">
                            <span className="font-semibold text-sm">Visible fields</span>
                            <button
                              type="button"
                              aria-label="Close field selector"
                              className="flex h-7 w-7 items-center justify-center rounded-full border border-slate-800 text-slate-800 hover:text-slate-900 transition hover:border-slate-900"
                              onClick={() => setShowFieldSelector(false)}
                            >
                              ×
                            </button>
                          </div>

                          <Checkbox.Group
                            options={fieldOptions}
                            value={selectedColumnKeys}
                            onChange={handleFieldSelectionChange}
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 10,
                              marginTop: 12,
                              maxHeight: 300,
                              overflowY: 'auto',
                            }}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div id="adminEventListing" className="mt-1">
                  {isLoading ? (
                    <Skeleton className="p-2" active title={{ width: '100%' }} paragraph={{ rows: 5, width: '100%' }} />
                  ) : (
                    <Table<EventStats>
                      pagination={{
                        pageSizeOptions: ['10', '20', '30', '50'],
                        showSizeChanger: true,
                        defaultPageSize: 20,
                      }}
                      bordered
                      className="eventTableRow"
                      rowKey={(record) => record.Id}
                      rowClassName={(record) => getEventStatusRowClassName(record.StatusId)}
                      columns={visibleColumns}
                      size="small"
                      dataSource={filteredDataSource}
                      scroll={{ x: 'max-content' }}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Suspense>
  )
}
